import { useEffect, useRef } from "react";

/**
 * Hero scene — vídeo Kling 2.5 com cross-fade dual-video pra loop seamless.
 *
 * Estratégia: dois <video> sobrepostos com a mesma fonte, offset por 5s.
 * Opacidade de cada um segue uma curva senoidal: 0 nas pontas, 1 no meio.
 *
 *   opacityA(t) = (1 - cos(2π·t/D)) / 2
 *   opacityB(t) = (1 - cos(2π·(t+D/2)/D)) / 2 = (1 + cos(2π·t/D)) / 2
 *
 * Soma garantida: opacityA + opacityB = 1 em qualquer momento.
 *
 * No frame do "cut" (t=0 ou t=10), aquele vídeo tem opacity=0 → o cut acontece
 * mas é invisível porque o OUTRO vídeo está em opacity=1 no meio do playback
 * estável. Sem reverse, sem regen, motion 100% natural.
 *
 * RAF tick atualiza opacidades por frame (~60Hz). Se os vídeos dessincronizam
 * por algum motivo, cada um lê own currentTime, então a curva auto-corrige.
 */
const DURATION = 10;

export function HeroScene() {
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const a = videoARef.current;
    const b = videoBRef.current;
    if (!a || !b) return;

    let raf = 0;

    // Configura B com offset de meia-duração assim que metadata carregar
    const onBLoaded = () => {
      b.currentTime = DURATION / 2;
      b.play().catch(() => {});
    };
    if (b.readyState >= 1) onBLoaded();
    else b.addEventListener("loadedmetadata", onBLoaded);

    const onALoaded = () => {
      a.play().catch(() => {});
    };
    if (a.readyState >= 1) onALoaded();
    else a.addEventListener("loadedmetadata", onALoaded);

    const tick = () => {
      const tA = a.currentTime;
      const tB = b.currentTime;
      const phaseA = (tA / DURATION) * Math.PI * 2;
      const phaseB = (tB / DURATION) * Math.PI * 2;
      a.style.opacity = String((1 - Math.cos(phaseA)) / 2);
      b.style.opacity = String((1 - Math.cos(phaseB)) / 2);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      a.removeEventListener("loadedmetadata", onALoaded);
      b.removeEventListener("loadedmetadata", onBLoaded);
    };
  }, []);

  const videoStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "right center",
    pointerEvents: "none",
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: -1,
        background: "#050810",
        overflow: "hidden",
      }}
    >
      <video
        ref={videoARef}
        src="/banners/painel-motion.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{ ...videoStyle, opacity: 1 }}
      />
      <video
        ref={videoBRef}
        src="/banners/painel-motion.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{ ...videoStyle, opacity: 0 }}
      />
    </div>
  );
}
